import { useMemo } from "react";

import { fromUnixTime } from "date-fns";

import { useFeatureFlagToggle } from "Reducers/featureFlags/useFeatureFlagToggle";
import {
  ServiceResourceData,
  ServiceResourceInstanceType
} from "src/api/observability/pipeline/types";
import { useServiceList } from "src/organization/pages/project/environment/metrics/profiling/util";

export const buildMetrics = (data: ServiceResourceData[] = []) => {
  const hosts = new Set<string>();

  const metrics = data
    .sort((a, b) => a.timestamp - b.timestamp)
    .reduce((accum, { instances, timestamp }) => {
      if (!instances) return accum;
      const unitsPointMetrics = getMetricsInstances(
        instances,
        fromUnixTime(timestamp)
      );
      Object.keys(instances).forEach(host => hosts.add(host));

      // Group metrics by unit (e.g. cpu, memory, mountpoints)
      for (const [unit, point] of Object.entries(unitsPointMetrics)) {
        if (!accum[unit]) accum[unit] = [];
        accum[unit].push(point);
      }
      return accum;
    }, {});

  const instances = Array.from(hosts).sort();

  return { metrics, instances };
};

const getMetricsInstances = (
  instances: ServiceResourceInstanceType,
  date: Date
) => {
  const instanceEntries = Object.entries(instances);
  const metricsByUnit: Record<string, any[]> = {};

  const addMetric = (
    unit: string,
    instance: string,
    value: number = 0,
    max: number = 0
  ) => {
    //add metrics to the unit if the unit does not exist, create it
    const percentage = max ? (value / max) * 100 : 0;

    if (!metricsByUnit[unit]) metricsByUnit[unit] = [];

    metricsByUnit[unit].push({
      instance,
      value,
      max,
      percentage,
      date
    });
  };

  for (const [instance, data] of instanceEntries) {
    if (!data) continue;
    //add for cpu and memory
    addMetric(
      "cpu",
      instance,
      data.cpu_used?.avg || 0,
      data.cpu_limit?.max || 0
    );
    addMetric(
      "memory",
      instance,
      data.memory_used?.avg || 0,
      data.memory_limit?.max || 0
    );

    //add for mountpoints
    for (const [mount, mp] of Object.entries(data.mountpoints || {})) {
      addMetric(
        mount,
        instance,
        mp.disk_used?.avg || 0,
        mp.disk_limit?.max || 0
      );
    }
  }

  const buildUnitSummary = (metrics: any[]) => {
    const totalValue = metrics.reduce((sum, m) => sum + m.value, 0);
    const totalPercentage = metrics.reduce((sum, m) => sum + m.percentage, 0);
    const count = metrics.length;

    const summary: Record<string, any> = {
      max: metrics[0]?.max || 0,
      date: metrics[0].date,
      "average@value": totalValue / count,
      "average@percentage": totalPercentage / count
    };

    for (const m of metrics) {
      summary[`${m.instance}@value`] = m.value;
      summary[`${m.instance}@percentage`] = m.percentage;
    }

    return summary;
  };

  const result: Record<string, any> = {};
  for (const [unit, metrics] of Object.entries(metricsByUnit)) {
    result[unit] = buildUnitSummary(metrics);
  }
  return result;
};

export const useStubService = () => {
  const { app, worker, service } = useServiceList().serviceList;

  const stub = useMemo(() => {
    const all = [...app, ...worker, ...service];

    if (all.length === 0) {
      return { list: [], app: [], worker: [], service: [] };
    }
    const ap = app.map(ap => ({ ...ap, kind: "webapp" }));
    const wk = worker.map(wk => ({ ...wk, kind: "worker" }));
    const sv = service.map(sv => ({ ...sv, kind: "service" }));

    return { all: [ap, wk, sv].flat(), app: ap, worker: wk, service: sv };
  }, [app, worker, service]);

  return stub;
};

export const useInternalStorageServices = () => {
  const [isInternalMetricsEnabled] = useFeatureFlagToggle(
    "ENABLE_INTERNAL_SERVICE_METRICS"
  );
  const { app, service, worker } = useStubService();

  if (!isInternalMetricsEnabled) {
    return [app, service, worker];
  }

  const routerService = {
    name: "router",
    kind: "router",
    icon: "router"
  };
  const servicesWithRouter = [...service, routerService];

  const addInternalStorage = service => [
    service,
    { ...service, name: `${service.name}---internal---storage` }
  ];

  return [app, servicesWithRouter, worker].map(services =>
    services.flatMap(addInternalStorage)
  );
};
